import React, {useState} from "react";
import PhoneInput, {CountryData} from "react-phone-input-2";
import {LangKey, useGlobalContext} from "../../hooks/globalContext";
import {useI18nText} from "../../hooks/input/i18nText";
import {I18nTranslate} from "../../utils/translate/translate";
import {useTranslate} from "../../hooks/input/useTranslate";
import {PhoneContact} from "../../models/other/phone";

interface Props {
    label: string
    value: PhoneContact[]
    onChange: (contacts: PhoneContact[]) => void
    lang: LangKey
    allLang: LangKey[]
    isStaff: boolean
}

interface CardProps {
    contact: PhoneContact
    lang: LangKey
    allLang: LangKey[]
    isStaff: boolean
    onChangeLabel: (label: { [key: string]: string } | null) => void
    onDelete: () => void
}

function PhoneContactCard({contact, lang, allLang, isStaff, onChangeLabel, onDelete}: CardProps) {
    const i18n = useI18nText(allLang, lang)
    const translate = useTranslate()
    const {i18n: txt} = useGlobalContext()
    const labelPlaceholder = txt.label_optional()

    function setLabel(values: { [key: string]: string }) {
        const hasAny = Object.values(values).some(v => v !== "")
        onChangeLabel(hasAny ? values : null)
    }

    const labelValues = contact.label || {}

    const [isTranslating, setIsTranslating] = useState(false)
    const [translateErrors, setTranslateErrors] = useState<string[]>([])
    const [translateNotice, setTranslateNotice] = useState("")

    const handleTranslate = async (e: React.MouseEvent) => {
        e.preventDefault()
        if (isTranslating) {
            return
        }
        setTranslateErrors([])
        setTranslateNotice("")

        const source = (labelValues[i18n.langs[0]] || "").trim()
        if (!source) {
            setTranslateNotice("Сначала заполните поле на языке " + i18n.langs[0] + ".")
            return
        }

        setIsTranslating(true)
        const failed: string[] = []
        let translated = 0
        try {
            await I18nTranslate(
                labelValues,
                i18n.langs[0],
                i18n.langs,
                (result) => { translated++; setLabel(result) },
                (lang) => { failed.push(lang) },
                translate,
            )
        } finally {
            setTranslateErrors(failed)
            if (translated === 0 && failed.length === 0) {
                setTranslateNotice("Все языки уже заполнены. Очистите поле нужного языка, чтобы перевести заново.")
            }
            setIsTranslating(false)
        }
    }

    return (
        <div className="control" style={{border: "1px solid #e0e0e0", borderRadius: "8px", padding: "12px", marginBottom: "8px"}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px"}}>
                <span style={{fontWeight: 500}}>{contact.number}</span>
                <span style={{cursor: "pointer", padding: "0 4px"}} onClick={onDelete}>x</span>
            </div>
            <div>
                <div style={{display: "flex"}}>
                    <input
                        className="control__input_i18n"
                        value={labelValues[i18n.langs[0]] || ""}
                        style={i18n.langs.length > 1 ? {width: "90%"} : {width: "100%"}}
                        type="text"
                        placeholder={labelPlaceholder}
                        onChange={(e) => {
                            setLabel({...labelValues, [i18n.langs[0]]: e.currentTarget.value})
                        }}
                    />
                    {i18n.langs.length > 1 && (
                        <>
                            <div
                                className="button -gray"
                                style={{width: "10%", padding: "0 20px"}}
                                onClick={() => i18n.setIsActive(!i18n.isActive)}
                            >
                                <p className="userLang" aria-expanded={i18n.isActive}>{i18n.langs[0]}</p>
                            </div>
                            {isStaff && (
                                <button
                                    type="button"
                                    disabled={isTranslating}
                                    title={isTranslating ? "Перевод..." : "Перевести"}
                                    className="button -gray"
                                    style={{width: "40px", height: "40px", padding: "10px",
                                            opacity: isTranslating ? 0.5 : 1}}
                                    onClick={handleTranslate}
                                >
                                    <svg width="64px" height="64px" viewBox="0 0 21 21" style={{margin: 0}}
                                         xmlns="http://www.w3.org/2000/svg" fill="#000000">
                                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                        <g id="SVGRepo_iconCarrier">
                                            <g fill="none" fillRule="evenodd" stroke="#646D7C" strokeLinecap="round"
                                               strokeLinejoin="round" transform="translate(2 2)">
                                                <path d="m16.5 8.5v-6c0-1.1045695-.8954305-2-2-2h-6c-1.1045695 0-2 .8954305-2 2v6c0 1.1045695.8954305 2 2 2h6c1.1045695 0 2-.8954305 2-2z"></path>
                                                <path d="m4.5 6.50344846h-2.00001427c-1.1045695 0-2 .8954305-2 2v5.99943324c0 1.1045695.8954305 2 2 2h.00345627l6.00001428-.0103718c1.10321833-.0019065 1.99654372-.8967771 1.99654372-1.999997v-1.9925129"></path>
                                                <g transform="translate(2.502 9.5)">
                                                    <path d="m2.998 1.003h-3"></path>
                                                    <path d="m4.49841597 2.5c-.33333333.33333333-.66666667.66666667-1 1s-1.16666667.83333333-2.5 1.5"></path>
                                                    <path d="m.99841597 1.00316806c.33333333 1.16613866.83333333 1.99894398 1.5 2.49841597s1.5.99894398 2.5 1.49841597"></path>
                                                </g>
                                                <g transform="translate(8.5 2.5)">
                                                    <path d="m3 0-3 6"></path>
                                                    <path d="m3 0 3 6"></path>
                                                    <path d="m3 2v4" transform="matrix(0 1 -1 0 7 1)"></path>
                                                </g>
                                            </g>
                                        </g>
                                    </svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
                {i18n.isActive && i18n.langs.filter(l => l !== i18n.langs[0]).map(l => (
                    <div style={{display: "flex"}} key={l}>
                        <input
                            className="control__input_i18n"
                            value={labelValues[l] || ""}
                            type="text"
                            placeholder={labelPlaceholder}
                            onChange={(e) => {
                                setLabel({...labelValues, [l]: e.currentTarget.value})
                            }}
                        />
                        <div className="button -disabled" style={{width: "10%", padding: "0 20px"}}>{l}</div>
                    </div>
                ))}
                {translateErrors.length > 0 ? (
                    <p className="control__error" style={{color: "#d33", fontSize: "12px", marginTop: "4px"}}>
                        Не удалось перевести на: {translateErrors.join(", ")}. Поле оставлено пустым — переведите вручную или попробуйте ещё раз.
                    </p>
                ) : null}
                {translateNotice ? (
                    <p style={{color: "#646D7C", fontSize: "12px", marginTop: "4px"}}>
                        {translateNotice}
                    </p>
                ) : null}
            </div>
        </div>
    )
}

export function MultiPhoneContactI18n(props: Props) {
    const {i18n: txt} = useGlobalContext()
    const [isAdding, setIsAdding] = useState(false)
    const [draft, setDraft] = useState<{number: string; countryCode: string}>({number: "", countryCode: ""})

    function commitDraft() {
        if (!draft.countryCode || draft.number === "") return
        props.onChange([...props.value, {number: draft.number, countryCode: draft.countryCode, label: null}])
        setDraft({number: "", countryCode: ""})
        setIsAdding(false)
    }

    function updateLabel(index: number, label: { [key: string]: string } | null) {
        const updated = props.value.map((c, i) => i === index ? {...c, label} : c)
        props.onChange(updated)
    }

    function remove(index: number) {
        props.onChange(props.value.filter((_, i) => i !== index))
    }

    return (
        <div className="control">
            <label className="control__label">{props.label}</label>

            {props.value.map((contact, index) => (
                <PhoneContactCard
                    key={index}
                    contact={contact}
                    lang={props.lang}
                    allLang={props.allLang}
                    isStaff={props.isStaff}
                    onChangeLabel={(label) => updateLabel(index, label)}
                    onDelete={() => remove(index)}
                />
            ))}

            {isAdding && (
                <div style={{display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px"}}>
                    <PhoneInput
                        inputClass="control__input hello"
                        value={draft.number}
                        onChange={(number, code: CountryData) => {
                            if (code) {
                                setDraft({number, countryCode: code.countryCode})
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commitDraft()
                        }}
                        specialLabel=""
                        prefix="+"
                        areaCodes={{kz: ["7"]}}
                    />
                    <button className="button -blue" onClick={(e) => { e.preventDefault(); commitDraft() }}>
                        {txt.add_phone()}
                    </button>
                    <button className="button -bordered_blue" onClick={(e) => { e.preventDefault(); setIsAdding(false); setDraft({number: "", countryCode: ""}) }}>
                        {txt.cancel()}
                    </button>
                </div>
            )}

            {!isAdding && (
                <button className="button -bordered_blue" style={{marginTop: "4px"}} onClick={(e) => { e.preventDefault(); setIsAdding(true) }}>
                    + {txt.add_phone()}
                </button>
            )}
        </div>
    )
}
